import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../context/LanguageContext';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import HelpCard from '../../../components/help-card/ui/HelpCard';
import { MobileInlineCta, MobileCtaBar } from '../../../components/mobile-cta/ui/MobileCta';
import { seoData } from '../../../../data/seoData';
import { trackPhoneClick } from '../../../components/email/utils/trackingUtils';
import '../style/pneus-page.css';

const PHONE = '+351219010100';
const PHONE_DISPLAY = '219 010 100';

const PneusPage: React.FC = () => {
    const { language } = useLanguage();
    const pt = language === 'pt';

    const faqs = pt
        ? [
            {
                q: 'Onde encontro a medida dos meus pneus?',
                a: 'Está gravada no flanco do pneu, numa sequência como 205/55 R16 91V, e também no manual do carro ou na etiqueta junto à porta do condutor. Se preferir, indique a matrícula e confirmamos.'
            },
            {
                q: 'Preciso de trocar os quatro ao mesmo tempo?',
                a: 'Não obrigatoriamente, mas os pneus do mesmo eixo devem ser iguais e ter desgaste semelhante. Quando se trocam apenas dois, montam-se no eixo traseiro, o que assegura melhor estabilidade em travagem.'
            },
            {
                q: 'A equilibragem é feita na montagem?',
                a: 'Sim. Todos os pneus que montamos são equilibrados.'
            },
            {
                q: 'Servem clientes de fora da Malveira?',
                a: 'Sim. Estamos na Malveira e recebemos também clientes de Mafra, da Venda do Pinheiro e das zonas próximas, para montagem de pneus e outros serviços de oficina automóvel.'
            }
        ]
        : [
            {
                q: 'Where do I find my tyre size?',
                a: 'It is engraved on the tyre sidewall, in a sequence like 205/55 R16 91V, and also in the car manual or on the label by the driver\'s door. If you prefer, give us the licence plate and we confirm it.'
            },
            {
                q: 'Do I need to change all four at the same time?',
                a: 'Not necessarily, but tyres on the same axle must be identical and have similar wear. When only two are replaced, they are fitted on the rear axle, which ensures better stability under braking.'
            },
            {
                q: 'Is balancing done at fitting?',
                a: 'Yes. Every tyre we fit is balanced.'
            },
            {
                q: 'Do you serve customers from outside Malveira?',
                a: 'Yes. We are in Malveira and also welcome customers from Mafra, Venda do Pinheiro and the surrounding areas, for tyre fitting and other car workshop services.'
            }
        ];

    const replacementSigns = pt
        ? [
            'A profundidade do desenho aproxima-se do mínimo legal de 1,6 mm, embora a aderência em piso molhado comece a reduzir-se antes disso.',
            'O desgaste apresenta-se desigual entre o interior e o exterior do pneu.',
            'Há fissuras nos flancos ou uma deformação visível.',
            'O carro puxa para um lado, o volante vibra, ou o ruído de rolamento aumentou.'
        ]
        : [
            'The tread depth is approaching the legal minimum of 1.6 mm, although wet grip starts to decrease before that.',
            'Wear is uneven between the inside and outside of the tyre.',
            'There are cracks in the sidewalls or visible deformation.',
            'The car pulls to one side, the steering wheel vibrates, or rolling noise has increased.'
        ];

    return (
        <>
            <SEO
                title={seoData.pneus.title}
                description={seoData.pneus.description}
                keywords={seoData.pneus.keywords}
                canonical={seoData.pneus.canonical}
            />
            <div className="pneus-page">
                <Navbar />

                {/* Page Header */}
                <section className="page-header">
                    <div
                        className="page-header__bg"
                        style={{
                            backgroundImage: 'url(/assets/logo/store/loja_2.webp)',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            height: '380px'
                        }}
                    ></div>
                    <div className="container">
                        <div className="page-header__inner">
                            <h3>{pt ? 'Pneus' : 'Tires'}</h3>
                            <div className="thm-breadcrumb__inner">
                                <ul className="thm-breadcrumb list-unstyled">
                                    <li><a href="/">{pt ? 'Início' : 'Home'}</a></li>
                                    <li><span className="fas fa-angle-right"></span></li>
                                    <li><span>{pt ? 'Pneus' : 'Tires'}</span></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Main Content Section */}
                <section className="pneus-main-section">
                    <div className="container">
                        {/* Introduction */}
                        <div className="pneus-intro">
                            <img
                                className="pneus-intro__tire"
                                src="/assets/images/pneus-tire.png"
                                alt={pt ? 'Pneu' : 'Tyre'}
                                loading="lazy"
                                width={585}
                                height={585}
                            />
                            <h1 className="pneus-intro__title">
                                {pt
                                    ? 'Pneus na Malveira: escolha, montagem e equilibragem'
                                    : 'Tyres in Malveira: choice, fitting and balancing'}
                            </h1>
                            <p className="pneus-intro__description">
                                {pt
                                    ? 'No Roady Malveira encontra pneus para automóveis ligeiros, com aconselhamento na escolha, montagem e equilibragem. Indique-nos a medida dos seus pneus e apresentamos as opções disponíveis. Trabalhamos com diferentes marcas e gamas de pneus, com soluções adequadas ao seu veículo, tipo de condução e orçamento. Consulte-nos para conhecer as opções e preços disponíveis.'
                                    : 'At Roady Malveira you will find tyres for passenger cars, with advice on choice, fitting and balancing. Give us your tyre size and we present the available options. We work with different tyre brands and ranges, with solutions suited to your vehicle, driving style and budget. Ask us about the available options and prices.'}
                            </p>
                            <MobileInlineCta
                                phone={PHONE}
                                phoneDisplay={PHONE_DISPLAY}
                                quoteHref="/orcamento?service=pneus"
                                source="pneus_page"
                                pt={pt}
                            />
                        </div>

                        {/* Aconselhamento antes da escolha */}
                        <div className="pneus-block">
                            <h2 className="pneus-block__title">
                                {pt ? 'Aconselhamento antes da escolha' : 'Advice before choosing'}
                            </h2>
                            <p className="pneus-block__text">
                                {pt
                                    ? 'Cada condução pede uma resposta diferente. Quem faz sobretudo percursos curtos de cidade desgasta os pneus de forma distinta de quem soma quilómetros de autoestrada todas as semanas. Consideramos as dimensões que o fabricante indica para o seu carro, o índice de carga e de velocidade, o tipo de percurso e a época do ano. Só depois apresentamos opções, e explicamos a diferença entre elas em linguagem simples.'
                                    : 'Every driving style calls for a different answer. Someone who mostly does short city trips wears tyres differently from someone who adds motorway kilometres every week. We consider the sizes the manufacturer indicates for your car, the load and speed ratings, the type of route and the season. Only then do we present options, and we explain the difference between them in plain language.'}
                            </p>
                        </div>

                        {/* Montagem, equilibragem e alinhamento */}
                        <div className="pneus-block">
                            <h2 className="pneus-block__title">
                                {pt ? 'Montagem, equilibragem e alinhamento' : 'Fitting, balancing and alignment'}
                            </h2>
                            <p className="pneus-block__text">
                                {pt
                                    ? <>A montagem termina sempre com equilibragem, para que não haja vibração no volante a velocidade de cruzeiro. Aproveitamos para verificar o estado das válvulas e a pressão de todos os pneus. Se o desgaste indicar um problema de geometria, propomos o <Link to="/oficina/alinhamento">alinhamento de direção</Link> na mesma visita, para que o pneu novo não repita o desgaste do anterior.</>
                                    : <>Fitting always ends with balancing, so there is no steering wheel vibration at cruising speed. We take the opportunity to check the condition of the valves and the pressure of all tyres. If the wear points to a geometry problem, we propose <Link to="/oficina/alinhamento">wheel alignment</Link> in the same visit, so the new tyre does not repeat the wear of the previous one.</>}
                            </p>
                        </div>

                        {/* Quando considerar a substituição */}
                        <div className="pneus-block">
                            <h2 className="pneus-block__title">
                                {pt ? 'Quando considerar a substituição' : 'When to consider replacement'}
                            </h2>
                            <ul className="pneus-block__list">
                                {replacementSigns.map((sign, index) => (
                                    <li key={index}>{sign}</li>
                                ))}
                            </ul>
                            <p className="pneus-block__text">
                                {pt
                                    ? 'Se reconhece algum destes pontos, fale connosco ou passe pela loja.'
                                    : 'If you recognise any of these points, talk to us or drop by the store.'}
                            </p>
                        </div>

                        {/* Perguntas frequentes */}
                        <div className="pneus-block">
                            <h2 className="pneus-block__title">
                                {pt ? 'Perguntas frequentes' : 'Frequently asked questions'}
                            </h2>
                            {faqs.map((faq, index) => (
                                <div key={index} className="pneus-faq-item">
                                    <h3 className="pneus-faq-item__question">{faq.q}</h3>
                                    <p className="pneus-faq-item__answer">{faq.a}</p>
                                </div>
                            ))}
                        </div>

                        {/* Buttons (B4) */}
                        <div className="pneus-cta">
                            <Link to="/orcamento?service=pneus" className="thm-btn pneus-cta__btn">
                                {pt ? 'Peça orçamento' : 'Request a Quote'}
                                <span><i className="icon-next"></i></span>
                            </Link>
                            <a
                                href="tel:+351219010100"
                                className="thm-btn pneus-cta__btn pneus-cta__btn--call"
                                onClick={() => trackPhoneClick('pneus_page')}
                            >
                                {pt ? 'Fale connosco' : 'Call us'}
                                <span><i className="icon-phone-call"></i></span>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Orçamento Card Section */}
                <section className="pneus-budget-section">
                    <div className="container">
                        <HelpCard />
                    </div>
                </section>

                <Footer />
                <ScrollToTop />
                <MobileCtaBar
                    phone={PHONE}
                    phoneDisplay={PHONE_DISPLAY}
                    quoteHref="/orcamento?service=pneus"
                    source="pneus_page"
                    pt={pt}
                />
            </div>
        </>
    );
};

export default PneusPage;
